import React, { useState, useRef, useEffect, useMemo } from 'react'
import { ProductPicker } from '../components/ProductPicker'
import { PartyEditModal } from '../components/PartyEditModal'
import { DateInputDMY } from '../components/DateInputDMY'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { apiClient } from '@services/api'
import { useAuthStore } from '@stores/authStore'
import { evalArithmetic } from '../utils/arithmetic'

type Unit = 'kg' | 'gms' | 'pkt' | 'Tin' | 'ltr'
const UNITS: Unit[] = ['kg', 'gms', 'pkt', 'Tin', 'ltr']

interface LineItem {
  id: string
  product: string
  route: 'Repacking' | 'Direct Sale'
  unit?: Unit
  qty: number
  rate: number
  gstRate?: number // GST % for this item; unset = the product's catalogue rate
}

interface Supplier {
  id: number
  name: string
  phone?: string | null
  location?: string | null
  address?: string | null
  gstin?: string | null
  email?: string | null
}

interface PurchaseBill {
  id: number
  billNo: string
  supplierName: string
  itemCount: number
  taxableAmount?: number
  gstRate?: number
  gstAmount?: number
  amount: number
  status: 'Active' | 'Draft' | 'Completed'
}

interface PurchaseOrder {
  id: number
  poNo: string
}

interface Product {
  id: number
  name: string
  defaultRoute: 'Repacking' | 'Direct Sale'
  unit: Unit
  gstRate?: number
  nameTamil?: string | null
  category?: string | null
  hsnCode?: string | null
}

const EMPTY_SUPPLIER = { name: '', phone: '', address: '', gstin: '' }
const EMPTY_PRODUCT = { name: '', nameTamil: '', category: '', hsnCode: '', gstRate: 0, unit: 'kg' as Unit }
const routeForUnit = (unit: Unit): LineItem['route'] => (unit === 'kg' ? 'Repacking' : 'Direct Sale')

const SAMPLE_LINE_ITEMS: LineItem[] = [
  { id: '1', product: 'Cardamom (Elaichi)', route: 'Repacking', qty: 50, rate: 1850 },
  { id: '2', product: 'Black Pepper', route: 'Repacking', qty: 25, rate: 640 },
  { id: '3', product: 'Turmeric Powder', route: 'Repacking', qty: 40, rate: 190 },
  { id: '4', product: 'Good Day Biscuits (Case)', route: 'Direct Sale', qty: 80, rate: 42 },
  { id: '5', product: 'Lifebuoy Soap (Case)', route: 'Direct Sale', qty: 60, rate: 28 },
]

const blankLine = (id: string): LineItem => ({ id, product: '', route: 'Repacking', qty: NaN, rate: NaN })

const formatINR = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`
const dmy = (iso: string) => (/^\d{4}-\d{2}-\d{2}/.test(iso) ? `${iso.slice(8, 10)}-${iso.slice(5, 7)}-${iso.slice(0, 4)}` : iso)
const money2 = (n: number) => `₹${(Math.round(n * 100) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export { evalArithmetic } // shared with the billing page; kept here for existing imports/tests
const unitFor = (item: Pick<LineItem, 'route' | 'unit'>): Unit => item.unit || (item.route === 'Repacking' ? 'kg' : 'pkt')

// The bill-reading (Auto-scan) feature is not built yet: the buttons stay hidden until a real reader is connected.
const AUTO_SCAN_ENABLED = false

export function PurchaseEntry() {
  const [entryMode, setEntryMode] = useState<'manual' | 'upload'>('manual')
  const [selectedSupplier, setSelectedSupplier] = useState<number | null>(null)
  const [supplierText, setSupplierText] = useState('') // what is typed in the supplier box; equals the name once matched
  const [billNo, setBillNo] = useState('')
  const [billDate, setBillDate] = useState(new Date().toISOString().split('T')[0])
  const [discountMode, setDiscountMode] = useState<'percent' | 'amount'>('percent')
  const [discountInput, setDiscountInput] = useState('') // blank = no discount
  const [gstIncluded, setGstIncluded] = useState(true) // false = estimate / non-GST supplier: no tax on the bill
  // true = the supplier's rates already include GST (Zoho "Tax Inclusive"): GST is shown as a breakup, not added on top.
  const [taxInclusive, setTaxInclusive] = useState<boolean>(() => { try { return localStorage.getItem('pe-tax-mode') !== 'exclusive' } catch { return true } })
  const chooseTaxMode = (inclusive: boolean) => { setTaxInclusive(inclusive); try { localStorage.setItem('pe-tax-mode', inclusive ? 'inclusive' : 'exclusive') } catch { /* private mode */ } }
  const [roundOffInput, setRoundOffInput] = useState('') // blank = auto round to the nearest rupee
  const [linkedPO, setLinkedPO] = useState<number | null>(null)
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [uploadPreview, setUploadPreview] = useState<string>('')
  const [isScanning, setIsScanning] = useState(false)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const repackingEnabled = (useAuthStore((s) => s.user) as any)?.tenant?.repackingEnabled !== false
  // Raw text typed in each line's Rate box (may be an expression like 600/60); the numeric rate is derived from it.
  const [rateText, setRateText] = useState<Record<string, string>>({})
  const rateDisplay = (item: LineItem) => (rateText[item.id] !== undefined ? rateText[item.id] : Number.isNaN(item.rate) ? '' : String(item.rate))
  const typeRate = (item: LineItem, text: string) => {
    setRateText((m) => ({ ...m, [item.id]: text }))
    const v = text.trim() === '' ? NaN : evalArithmetic(text)
    handleLineItemChange(item.id, 'rate', v === null ? NaN : v)
  }
  const settleRate = (item: LineItem) => {
    const text = rateText[item.id]
    if (text === undefined) return
    const v = text.trim() === '' ? NaN : evalArithmetic(text)
    setRateText((m) => { const n = { ...m }; if (v === null || Number.isNaN(v)) { if (text.trim() === '') delete n[item.id] } else n[item.id] = String(v); return n })
    if (v !== null && !Number.isNaN(v)) handleLineItemChange(item.id, 'rate', v)
  }
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [lineItems, setLineItems] = useState<LineItem[]>([blankLine('1')])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [editSupplier, setEditSupplier] = useState(false) // "Edit supplier" dialog for the selected supplier
  const [showNewSupplier, setShowNewSupplier] = useState(false)
  const [newSupplier, setNewSupplier] = useState(EMPTY_SUPPLIER)
  const [newSupplierError, setNewSupplierError] = useState('')
  const [savingSupplier, setSavingSupplier] = useState(false)
  const [showNewProduct, setShowNewProduct] = useState(false)
  const [newProduct, setNewProduct] = useState(EMPTY_PRODUCT)
  const [newProductError, setNewProductError] = useState('')
  const [savingProduct, setSavingProduct] = useState(false)

  const { data: suppliersData, refetch: refetchSuppliers } = useQuery({
    queryKey: ['suppliers-list'],
    queryFn: () => apiClient.get<any>('/suppliers'),
  })
  const { data: productsData, refetch: refetchProducts } = useQuery({
    queryKey: ['products-list'],
    queryFn: () => apiClient.get<any>('/products'),
  })
  const { data: purchaseOrdersData } = useQuery({
    queryKey: ['purchase-orders-list'],
    queryFn: () => apiClient.get<any>('/purchase-orders'),
  })
  const { data: recentBillsData, refetch: refetchRecentBills } = useQuery({
    queryKey: ['recent-purchase-bills'],
    queryFn: () => apiClient.get<any>('/purchase-bills?recent=true'),
  })
  // Bills parked as drafts (not in stock yet) — shown at the top until they are submitted or deleted.
  const { data: draftsData } = useQuery({
    queryKey: ['purchase-bills-drafts'],
    queryFn: () => apiClient.get<any>('/purchase-bills?status=Draft'),
  })
  const draftBills: PurchaseBill[] = draftsData?.data?.items || []

  const suppliers: Supplier[] = suppliersData?.data?.items || []
  const supplierOptions = useMemo(() => suppliers.map((s: any) => ({ value: String(s.id), label: s.name, sub: [s.location, s.phone].filter(Boolean).join(' · ') || null, right: s.gstin ? 'GST' : null })), [suppliers])
  // Keep the typed name in step with a supplier chosen elsewhere (PO pre-fill, "Add new supplier", reset).
  useEffect(() => {
    const s = suppliers.find((x) => x.id === selectedSupplier)
    if (s) setSupplierText(s.name)
    else if (selectedSupplier === null && supplierText && suppliers.some((x) => x.name === supplierText)) setSupplierText('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSupplier, suppliers])
  const typeSupplier = (text: string) => {
    setSupplierText(text)
    const match = suppliers.find((s) => s.name.toLowerCase() === text.trim().toLowerCase())
    setSelectedSupplier(match ? match.id : null)
    if (match) setValidationErrors((prev) => { const n = { ...prev }; delete n.supplier; return n })
  }
  const purchaseOrders: PurchaseOrder[] = purchaseOrdersData?.data?.items || []
  const recentBills: PurchaseBill[] = recentBillsData?.data?.items || []
  const [submittingDraftId, setSubmittingDraftId] = useState<number | null>(null)
  const submitDraftBill = async (bill: PurchaseBill) => {
    if (!window.confirm(`Submit ${bill.billNo} to stock?`)) return
    setSubmittingDraftId(bill.id); setErrorMessage('')
    try {
      const res: any = await apiClient.post('/purchase-bills/' + bill.id + '/submit', {})
      setSuccessMessage(res?.message || `${bill.billNo} submitted — stock updated.`)
      setSuccessLink('/dashboard/stock')
      refetchRecentBills()
      queryClient.invalidateQueries({ queryKey: ['purchase-bills-drafts'] })
      for (const k of ['purchase-bills', 'stock-overview', 'billing-items', 'repack-pending']) queryClient.invalidateQueries({ queryKey: [k] })
      setTimeout(() => setSuccessMessage(''), 5000)
    } catch (error: any) { setErrorMessage(error.response?.data?.message || 'Could not submit the draft bill') } finally { setSubmittingDraftId(null) }
  }
  const products: Product[] = productsData?.data?.items || []
  const categories = [...new Set(products.map((p: any) => p.category as string | null).filter(Boolean) as string[])].sort()
  const pickerOptions = useMemo(() => products.map((p: any) => ({ value: p.name, label: p.name, sub: p.nameTamil || null, right: p.unit === 'kg' ? 'per kg · repacking' : `per ${p.unit} · direct sale`, rightSub: p.category || null })), [products])

  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [successLink, setSuccessLink] = useState('')
  // ?edit=<id> reopens an existing bill in this form; saving sends PUT (status unchanged, stock re-posted).
  const editId = searchParams.get('edit')
  const { data: editData, isError: editMissing } = useQuery({ queryKey: ['purchase-bill', editId], queryFn: () => apiClient.get<any>(`/purchase-bills/${editId}`), enabled: !!editId, retry: false })
  const editingBill = editData?.data
  const editLoadedFor = useRef<string | null>(null)
  useEffect(() => {
    if (!editId || !editingBill || editLoadedFor.current === editId) return
    editLoadedFor.current = editId
    setEntryMode('manual'); setValidationErrors({}); setErrorMessage(''); setSuccessMessage('')
    setSelectedSupplier(editingBill.supplierId); setSupplierText(editingBill.supplierName || '')
    setBillNo(editingBill.billNo || ''); setBillDate(editingBill.billDate || new Date().toISOString().split('T')[0])
    setGstIncluded(editingBill.gstIncluded ?? (editingBill.gstRate || 0) > 0); setTaxInclusive(!!editingBill.taxInclusive)
    setDiscountMode(editingBill.discountMode === 'amount' ? 'amount' : 'percent'); setDiscountInput(editingBill.discountValue ? String(editingBill.discountValue) : '')
    setRoundOffInput(editingBill.roundOff ? String(editingBill.roundOff) : '')
    setLinkedPO(editingBill.linkedPOId || null)
    setRateText({})
    setLineItems((editingBill.lineItems || []).map((l: any, i: number) => ({ id: String(i + 1), product: l.product, route: l.route === 'Repacking' ? 'Repacking' : 'Direct Sale', unit: (l.unit || undefined) as Unit | undefined, qty: Number(l.quantity), rate: Number(l.rate), gstRate: l.gstRate ?? editingBill.gstRate ?? 0 })))
  }, [editId, editingBill])
  const cancelEdit = () => navigate(editId ? `/dashboard/procurement/purchase-bills/${editId}` : '/dashboard/procurement/purchase-bills')
  const { data: stockData } = useQuery({ queryKey: ['stock-overview'], queryFn: () => apiClient.get<any>('/stock/overview') })
  const stockRows: Array<{ product: string; packG: number; unit?: string; onHand: number; reorderPoint: number; status: string }> = stockData?.data?.finishedGoods || []
  const stockHint = (product: string) => {
    const rows = stockRows.filter((r) => r.product.toLowerCase() === product.trim().toLowerCase())
    if (rows.length === 0) return null
    return rows
      .map((r) => `${r.packG === 0 ? r.unit || 'pkt' : r.packG >= 1000 ? `${r.packG / 1000}kg` : `${r.packG}g`} ${r.onHand.toLocaleString('en-IN')}${r.status === 'Low' ? ' (Low)' : ''}`)
      .join(' · ')
  }

  // Deep link from Stock for Sale: ?product=… pre-fills the first line with that product.
  React.useEffect(() => {
    const preset = searchParams.get('product')
    if (!preset || products.length === 0) return
    const match = products.find((p) => p.name.toLowerCase() === preset.toLowerCase())
    setRateText({})
    setLineItems([{ id: '1', product: match?.name || preset, route: match?.defaultRoute || 'Repacking', qty: NaN, rate: NaN }])
    setSearchParams({}, { replace: true })
  }, [searchParams, products])

  // Linking a PO (dropdown or ?po=… from the Purchase Orders page) pre-fills supplier and the remaining quantities.
  const applyPurchaseOrder = async (poId: number | null) => {
    setLinkedPO(poId)
    setRateText({})
    if (!poId) return
    try {
      const res = await apiClient.get<any>(`/purchase-orders/${poId}`)
      const po = res?.data
      if (!po) return
      if (po.supplierId) setSelectedSupplier(po.supplierId)
      const remaining = (po.lines || []).filter((l: any) => l.remaining > 0)
      const src = remaining.length ? remaining : po.lines || []
      if (src.length) {
        setLineItems(src.map((l: any, i: number) => ({ id: String(i + 1), product: l.product, route: products.find((p) => p.name === l.product)?.defaultRoute || 'Direct Sale', unit: (l.unit || 'kg') as Unit, qty: remaining.length ? l.remaining : l.quantity, rate: l.rate })))
      }
      setSuccessMessage(`${po.poNumber} linked — ${src.length} line${src.length === 1 ? '' : 's'} pre-filled with ${remaining.length ? 'the quantity still to receive' : 'the ordered quantity'}; adjust to what actually arrived.`)
      setTimeout(() => setSuccessMessage(''), 5000)
    } catch {
      /* keep the manual entry */
    }
  }
  React.useEffect(() => {
    const po = searchParams.get('po')
    if (!po) return
    applyPurchaseOrder(Number(po))
    setSearchParams({}, { replace: true })
  }, [searchParams])

  const GST_RATES = [0, 5, 12, 18, 28]
  const r2 = (n: number) => Math.round(n * 100) / 100
  const productGst = (name: string) => products.find((p) => p.name.toLowerCase() === name.trim().toLowerCase())?.gstRate
  // The GST % that applies to a line: what was picked on it, else the product's catalogue rate, else 5%.
  const lineGst = (item: LineItem) => (gstIncluded ? (item.gstRate ?? productGst(item.product) ?? 5) : 0)
  const billTotal = r2(lineItems.reduce((sum, item) => sum + (item.qty || 0) * (item.rate || 0), 0)) // sub total of the items
  const discountValue = discountInput.trim() === '' ? 0 : Math.max(0, Number(discountInput) || 0)
  const discountAmount = Math.min(billTotal, discountMode === 'percent' ? r2(billTotal * Math.min(discountValue, 100) / 100) : r2(discountValue))
  const taxableValue = r2(billTotal - discountAmount)
  const discountRatio = billTotal > 0 ? taxableValue / billTotal : 1
  const taxBreakup = (() => {
    const groups = new Map<number, number>()
    for (const item of lineItems) groups.set(lineGst(item), (groups.get(lineGst(item)) || 0) + r2((item.qty || 0) * (item.rate || 0)))
    const rates = [...groups.keys()].sort((a, b) => a - b)
    let left = taxableValue
    return rates.map((rate, i) => {
      const base = i === rates.length - 1 ? r2(left) : r2((groups.get(rate) || 0) * discountRatio)
      left = r2(left - base)
      // Inclusive: the rate already holds the GST, so taxable = base ÷ (1 + rate%). Exclusive: GST goes on top.
      const taxable = taxInclusive ? r2(base / (1 + rate / 100)) : base
      const gst = taxInclusive ? r2(base - taxable) : r2(base * rate / 100)
      const cgst = r2(gst / 2)
      return { rate, taxable, gst, cgst, sgst: r2(gst - cgst) }
    })
  })()
  const gstAmount = r2(taxBreakup.reduce((s, t) => s + t.gst, 0))
  const usedRates = taxBreakup.map((t) => t.rate)
  const effectiveGstRate = usedRates.length ? Math.max(...usedRates) : 0
  const taxableExclGst = r2(taxBreakup.reduce((s, t) => s + t.taxable, 0))
  const beforeRounding = taxInclusive ? taxableValue : r2(taxableValue + gstAmount)
  // Rates include GST: the grand total equals the sub total unless a round-off is typed in; otherwise round to the nearest rupee.
  const autoRoundOff = gstIncluded && taxInclusive ? 0 : Math.round((Math.round(beforeRounding) - beforeRounding) * 100) / 100
  const roundOff = roundOffInput.trim() === '' ? autoRoundOff : Math.round((Number(roundOffInput) || 0) * 100) / 100
  const grandTotal = Math.round((beforeRounding + roundOff) * 100) / 100
  const repackingCount = lineItems.filter((item) => item.route === 'Repacking').length
  const directSaleCount = lineItems.filter((item) => item.route === 'Direct Sale').length
  const itemsSummary = repackingEnabled ? `${lineItems.length} item${lineItems.length === 1 ? '' : 's'} · ${repackingCount} Repacking, ${directSaleCount} Direct Sale` : `${lineItems.length} item${lineItems.length === 1 ? '' : 's'} · all to Stock for Sale`

  const runAutoScan = async (file: File) => {
    setIsScanning(true)
    setErrorMessage('')
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500))
      setLineItems(SAMPLE_LINE_ITEMS)
      setSuccessMessage(`Read ${file.name} — items matched to products and filled below. Please check before submitting.`)
      setTimeout(() => setSuccessMessage(''), 4000)
    } catch {
      setErrorMessage('Could not read the bill. Please enter it manually.')
    } finally {
      setIsScanning(false)
    }
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadedFile(file)
    setEntryMode('upload')
    if (file.type.startsWith('image/')) {
      const reader = new FileReader()
      reader.onload = (event) => setUploadPreview(event.target?.result as string)
      reader.readAsDataURL(file)
    } else {
      setUploadPreview('')
    }
    runAutoScan(file)
  }

  const handleLineItemChange = (id: string, field: keyof LineItem, value: any) => {
    setLineItems((items) => items.map((item) => (item.id === id ? { ...item, [field]: value } : item)))
    const key = `item-${id}-${field}`
    if (validationErrors[key]) {
      setValidationErrors((prev) => {
        const updated = { ...prev }
        delete updated[key]
        return updated
      })
    }
  }

  // After a row is added, move the cursor to its Product field so the biller can keep typing.
  const focusLineId = useRef<string | null>(null)
  useEffect(() => {
    if (!focusLineId.current) return
    const el = document.querySelector<HTMLInputElement>(`input[data-product-line="${focusLineId.current}"]`)
    focusLineId.current = null
    el?.focus()
  }, [lineItems])

  const handleAddLineItem = () => {
    const newId = (Math.max(0, ...lineItems.map((i) => parseInt(i.id))) + 1).toString()
    focusLineId.current = newId
    setLineItems([...lineItems, blankLine(newId)])
  }

  // Tab out of the Rate field on the last filled row → add the next row and jump to its Product field.
  const handleRateKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, item: LineItem) => {
    if (e.key !== 'Tab' || e.shiftKey) return
    const isLast = lineItems[lineItems.length - 1]?.id === item.id
    if (!isLast || !item.product.trim()) return
    e.preventDefault()
    handleAddLineItem()
  }

  const handleRemoveLineItem = (id: string) => {
    if (lineItems.length > 1) setLineItems((items) => items.filter((item) => item.id !== id))
  }

  const handleProductChange = (id: string, name: string) => {
    const match = products.find((p) => p.name.toLowerCase() === name.trim().toLowerCase())
    setLineItems((items) =>
      items.map((item) => (item.id === id ? { ...item, product: name, ...(match ? { route: match.defaultRoute, unit: match.unit } : {}) } : item))
    )
    const key = `item-${id}-product`
    if (validationErrors[key]) {
      setValidationErrors((prev) => {
        const updated = { ...prev }
        delete updated[key]
        return updated
      })
    }
  }

  const handleSaveSupplier = async () => {
    setNewSupplierError('')
    if (!newSupplier.name.trim()) {
      setNewSupplierError('Supplier name is required')
      return
    }
    if (newSupplier.phone && !/^\d{10}$/.test(newSupplier.phone)) {
      setNewSupplierError('Phone must be a 10-digit mobile number')
      return
    }
    setSavingSupplier(true)
    try {
      const res = await apiClient.post<any>('/suppliers', newSupplier)
      const created = res?.data
      await refetchSuppliers()
      if (created?.id) setSelectedSupplier(created.id)
      setNewSupplier(EMPTY_SUPPLIER)
      setShowNewSupplier(false)
      setSuccessMessage(`Supplier "${created?.name || newSupplier.name}" saved and selected for this bill.`)
      setTimeout(() => setSuccessMessage(''), 3000)
    } catch (error: any) {
      setNewSupplierError(error.response?.data?.message || 'Failed to save supplier')
    } finally {
      setSavingSupplier(false)
    }
  }

  // ---- "⋯ → Edit item / View item details" on every bill line: change the product's GST / HSN / names without leaving the bill ----
  const [menuFor, setMenuFor] = useState<string | null>(null)
  const [itemModal, setItemModal] = useState<{ lineId: string; mode: 'edit' | 'view' } | null>(null)
  const [itemForm, setItemForm] = useState({ name: '', gstRate: 0, hsnCode: '', nameTamil: '', category: '' })
  const [itemSaving, setItemSaving] = useState(false)
  const [itemError, setItemError] = useState('')
  const modalLine = itemModal ? lineItems.find((x) => x.id === itemModal.lineId) : undefined
  const modalProduct = modalLine ? products.find((p) => p.name.toLowerCase() === modalLine.product.trim().toLowerCase()) : undefined
  const openItemModal = (line: LineItem, mode: 'edit' | 'view') => {
    const prod = products.find((p) => p.name.toLowerCase() === line.product.trim().toLowerCase())
    setItemForm({ name: prod?.name || line.product, gstRate: line.gstRate ?? prod?.gstRate ?? 5, hsnCode: prod?.hsnCode || '', nameTamil: prod?.nameTamil || '', category: prod?.category || '' })
    setItemError(''); setMenuFor(null); setItemModal({ lineId: line.id, mode })
  }
  const saveItem = async () => {
    if (!modalProduct || !modalLine) return setItemError('This item is not in the product catalogue, so it cannot be edited here.')
    const newName = itemForm.name.replace(/\s+/g, ' ').trim()
    if (!newName) return setItemError('Item name cannot be empty')
    const hsn = itemForm.hsnCode.replace(/\s+/g, '')
    if (hsn && !/^\d{4,8}$/.test(hsn)) return setItemError('HSN code should be 4 to 8 digits (e.g. 0713 or 09109100)')
    setItemSaving(true); setItemError('')
    try {
      await apiClient.patch<any>(`/products/${modalProduct.id}`, { name: newName, category: itemForm.category, nameTamil: itemForm.nameTamil, hsnCode: hsn, gstRate: itemForm.gstRate })
      await refetchProducts()
      queryClient.invalidateQueries({ queryKey: ['billing-items'] })
      // Every line of this product picks up the new name and GST straight away.
      const oldName = modalProduct.name.toLowerCase()
      setLineItems((items) => items.map((x) => (x.product.trim().toLowerCase() === oldName ? { ...x, product: newName, gstRate: itemForm.gstRate } : x)))
      setItemModal(null)
    } catch (err: any) { setItemError(err.response?.data?.message || 'Could not save the item') } finally { setItemSaving(false) }
  }

  // "+ Add new item" from a line's picker: open the add-product panel with the typed name, and put the product on that line once saved.
  const newProductForLine = useRef<string | null>(null)
  const openNewProductFor = (lineId: string, typed: string) => {
    newProductForLine.current = lineId
    setNewProduct({ ...EMPTY_PRODUCT, name: typed.trim() })
    setNewProductError('')
    setShowNewProduct(true)
    setTimeout(() => { const el = document.querySelector<HTMLInputElement>('[data-testid=new-product-name]'); el?.scrollIntoView({ block: 'center' }); el?.focus() }, 50)
  }

  const handleSaveProduct = async () => {
    setNewProductError('')
    if (!newProduct.name.trim()) {
      setNewProductError('Product name is required')
      return
    }
    if (!/^\d{4,8}$/.test(newProduct.hsnCode.replace(/\s+/g, ''))) {
      setNewProductError('HSN code is required — 4 to 8 digits, as printed on the supplier bill (e.g. 0713 or 09109100)')
      return
    }
    setSavingProduct(true)
    try {
      const res = await apiClient.post<any>('/products', newProduct)
      const created: Product | undefined = res?.data
      await refetchProducts()
      const newId = (Math.max(0, ...lineItems.map((i) => parseInt(i.id))) + 1).toString()
      const emptyOnly = lineItems.length === 1 && !lineItems[0].product.trim()
      const newLine: LineItem = {
        id: newId,
        product: created?.name || newProduct.name.trim(),
        route: created?.defaultRoute || routeForUnit(newProduct.unit),
        unit: created?.unit || newProduct.unit,
        qty: NaN,
        rate: NaN,
      }
      const target = newProductForLine.current
      newProductForLine.current = null
      if (target && lineItems.some((i) => i.id === target)) {
        // Fill the line the user was typing on instead of adding another row.
        setLineItems((items) => items.map((i) => (i.id === target ? { ...i, product: newLine.product, route: newLine.route, unit: newLine.unit } : i)))
      } else {
        setLineItems(emptyOnly ? [newLine] : [...lineItems, newLine])
      }
      setNewProduct(EMPTY_PRODUCT)
      setShowNewProduct(false)
      setSuccessMessage(`Product "${newLine.product}" added to the catalogue and to this bill.`)
      setTimeout(() => setSuccessMessage(''), 3000)
    } catch (error: any) {
      setNewProductError(error.response?.data?.message || 'Failed to save product')
    } finally {
      setSavingProduct(false)
    }
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}
    if (!selectedSupplier) errors['supplier'] = 'Supplier is required'
    if (!billNo.trim()) errors['billNo'] = 'Bill number is required'
    if (!billDate) errors['billDate'] = 'Bill date is required'
    if (lineItems.length === 0) errors['lineItems'] = 'At least one line item is required'
    lineItems.forEach((item) => {
      if (!item.product.trim()) errors[`item-${item.id}-product`] = 'Product name is required'
      if (!(item.qty > 0)) errors[`item-${item.id}-qty`] = 'Quantity must be greater than 0'
      if (item.rate < 0) errors[`item-${item.id}-rate`] = 'Rate cannot be negative'
    })
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }


  const resetForm = () => {
    setSelectedSupplier(null)
    setSupplierText('')
    setRateText({})
    setBillNo('')
    setBillDate(new Date().toISOString().split('T')[0])
    setDiscountMode('percent'); setDiscountInput('')
    setGstIncluded(true)
    setRoundOffInput('')
    setLinkedPO(null)
    setLineItems([blankLine('1')])
    setUploadedFile(null)
    setUploadPreview('')
    setValidationErrors({})
    setEntryMode('manual')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleSubmit = async (e: React.FormEvent, isDraft = false, submitAfterSave = false) => {
    e.preventDefault()
    setErrorMessage('')
    if (!validateForm()) {
      setErrorMessage('Please fix the highlighted fields')
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    setSubmitting(true)
    try {
      const body = {
        supplierId: selectedSupplier,
        billNo,
        billDate,
        linkedPOId: linkedPO || null,
        lineItems: lineItems.map((item) => ({
          product: item.product,
          route: item.route,
          unit: unitFor(item),
          quantity: item.qty,
          rate: item.rate,
          amount: item.qty * item.rate,
          gstRate: lineGst(item),
        })),
        discountMode,
        discountValue,
        taxInclusive,
        taxableAmount: taxableValue,
        gstIncluded,
        gstRate: effectiveGstRate,
        gstAmount,
        roundOff,
        totalAmount: grandTotal,
        status: isDraft ? 'Draft' : 'Active',
      }
      if (editId) {
        const res: any = await apiClient.put(`/purchase-bills/${editId}`, body)
        let message = res?.message
        if (submitAfterSave) {
          // "Save & submit" on a draft: the corrected lines are saved first, then the bill goes to stock.
          const sub: any = await apiClient.post(`/purchase-bills/${editId}/submit`, {})
          message = sub?.message || message
        }
        queryClient.invalidateQueries({ queryKey: ['purchase-bill', editId] })
        for (const k of ['purchase-bills', 'purchase-bills-drafts', 'recent-purchase-bills', 'stock-overview', 'billing-items', 'repack-pending', 'dashboard-home', 'outstanding-bills']) queryClient.invalidateQueries({ queryKey: [k] })
        navigate(`/dashboard/procurement/purchase-bills/${editId}`, { state: { message } })
        return
      }
      await apiClient.post('/purchase-bills', body)
      setSuccessMessage(isDraft ? 'Purchase bill saved as draft.' : 'Purchase bill submitted — stock updated.')
      setSuccessLink(isDraft ? '' : '/dashboard/stock')
      resetForm()
      refetchRecentBills()
      queryClient.invalidateQueries({ queryKey: ['purchase-bills-drafts'] })
      setTimeout(() => setSuccessMessage(''), 3000)
    } catch (error: any) {
      setErrorMessage(error.response?.data?.message || 'Failed to submit purchase bill')
      window.scrollTo({ top: 0, behavior: 'smooth' }) // the message sits at the top of the form; the Save button is at the bottom
    } finally {
      setSubmitting(false)
    }
  }

  const fieldError = (key: string) =>
    validationErrors[key] ? <div className="hint-error">{validationErrors[key]}</div> : null

  const statusBadge = (status: PurchaseBill['status']) =>
    status === 'Active' ? 'badge-full' : status === 'Draft' ? 'badge-draft' : 'badge-ok'

  return (
    <div>
      {editSupplier && (() => {
        const sup: any = suppliers.find((x) => x.id === selectedSupplier)
        if (!sup) return null
        return (
          <PartyEditModal
            kind="supplier"
            initial={{ name: sup.name, phone: sup.phone || '', address: sup.address || '', location: sup.location || '', gstin: sup.gstin || '', email: sup.email || '' }}
            onClose={() => setEditSupplier(false)}
            onSave={async (v) => {
              const res = await apiClient.patch<any>(`/suppliers/${sup.id}`, { name: v.name, phone: v.phone, address: v.address, location: v.location, gstin: v.gstin, email: v.email })
              await refetchSuppliers()
              for (const k of ['suppliers-list', 'recent-purchase-bills', 'purchase-bills', 'purchase-bills-drafts', 'purchase-orders-list', 'stock-overview']) queryClient.invalidateQueries({ queryKey: [k] })
              setEditSupplier(false)
              setSuccessMessage(res.message || 'Supplier details saved')
              setTimeout(() => setSuccessMessage(''), 4000)
            }}
          />
        )
      })()}
      {itemModal && modalLine && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => !itemSaving && setItemModal(null)}>
          <div className="card" style={{ width: 'min(520px, 100%)', maxHeight: '90vh', overflow: 'auto', margin: 0 }} onClick={(e) => e.stopPropagation()} data-testid="item-modal">
            <div className="field-label-row">
              <h2 style={{ marginBottom: 0 }}>{itemModal.mode === 'edit' ? 'Edit item' : 'Item details'}</h2>
              <button type="button" className="btn-ghost" style={{ padding: '2px 9px' }} onClick={() => setItemModal(null)} aria-label="Close">×</button>
            </div>
            {itemError && <div className="alert alert-error" style={{ marginTop: 8 }}>{itemError}</div>}
            {!modalProduct && <div className="alert alert-error" style={{ marginTop: 8 }}>This item is not in the product catalogue, so it cannot be edited here.</div>}
            <label style={{ marginTop: 8 }}>Item name</label>
            <input type="text" disabled={itemModal.mode === 'view'} value={itemForm.name} data-testid="item-name" onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} />
            {itemModal.mode === 'edit' && <div className="hint" style={{ marginTop: 0 }}>Renaming also updates this item in past bills, invoices and stock.</div>}
            {modalProduct && <div className="hint" style={{ marginTop: 2 }}>Bought per {modalProduct.unit === 'pkt' ? 'piece' : modalProduct.unit} · {modalProduct.defaultRoute === 'Repacking' ? 'repacked into packs' : 'sold as purchased'}{stockHint(modalLine.product) ? ` · on hand ${stockHint(modalLine.product)}` : ''}</div>}
            <div className="grid grid-cols-2 gap-3" style={{ marginTop: 10 }}>
              <div>
                <label>GST %</label>
                <select disabled={itemModal.mode === 'view'} value={itemForm.gstRate} onChange={(e) => setItemForm({ ...itemForm, gstRate: Number(e.target.value) })}>{GST_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}</select>
              </div>
              <div>
                <label>HSN</label>
                <input type="text" inputMode="numeric" disabled={itemModal.mode === 'view'} value={itemForm.hsnCode} data-testid="item-hsn" onChange={(e) => setItemForm({ ...itemForm, hsnCode: e.target.value.replace(/\D/g, '').slice(0, 8) })} />
              </div>
              <div>
                <label>Category</label>
                <input type="text" list="product-categories" disabled={itemModal.mode === 'view'} value={itemForm.category} onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })} />
              </div>
              <div>
                <label>Tamil name</label>
                <input type="text" disabled={itemModal.mode === 'view'} value={itemForm.nameTamil} onChange={(e) => setItemForm({ ...itemForm, nameTamil: e.target.value })} />
              </div>
            </div>
            <div className="btnrow">
              {itemModal.mode === 'edit' && <button type="button" className="btn-accent" disabled={itemSaving || !modalProduct} onClick={saveItem} data-testid="item-save">{itemSaving ? 'Saving…' : 'Save'}</button>}
              <button type="button" className="btn-ghost" disabled={itemSaving} onClick={() => setItemModal(null)}>{itemModal.mode === 'view' ? 'Close' : 'Cancel'}</button>
            </div>
            <div className="hint">Changes apply to the product everywhere — the selling price is set on the Billing page or under Settings → Products &amp; Categories.</div>
          </div>
        </div>
      )}
      <div className="hdr">
        <h1>{editId ? `Edit bill ${editingBill?.billNo || ''}` : 'Purchase Entry'}</h1>
        <p>{editId ? `Change the supplier, bill number, date, GST or items and save. ${editingBill?.status === 'Draft' ? 'The bill stays a draft.' : 'Stock from the old lines is removed and re-posted from the new ones.'}` : 'One bill per supplier, with as many line items as the bill actually has. Each item is routed to Repacking or Direct Sale.'}</p>
      </div>
      {editId && editMissing && <div className="alert alert-error">That bill could not be loaded. <Link to="/dashboard/procurement/purchase-bills/list" className="underline">Back to bills</Link></div>}
      {editId && editingBill && editingBill.editable === false && <div className="alert alert-error">{editingBill.billNo} can no longer be edited: {editingBill.editBlockedReason}</div>}

      {successMessage && (
        <div className="alert alert-success">
          {successMessage}
          {successLink && (
            <>
              {' '}
              <Link to={successLink} className="underline">
                View Stock for Sale →
              </Link>
            </>
          )}
        </div>
      )}
      {errorMessage && <div className="alert alert-error">{errorMessage}</div>}

      {draftBills.filter((b) => String(b.id) !== editId).length > 0 && (
        <div className="card mb-[18px]" style={{ borderColor: '#E9C46A', background: '#FFFBEB' }} data-testid="drafts-panel">
          <h2>
            Draft bills waiting <small>— {draftBills.filter((b) => String(b.id) !== editId).length} saved as draft, not in stock yet. Open one to check and submit it, or delete it from its page.</small>
          </h2>
          <table>
            <thead>
              <tr>
                <th>Bill</th>
                <th>Supplier</th>
                <th>Bill date</th>
                <th className="num">Items</th>
                <th className="num">Grand Total</th>
                <th>Saved by</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {draftBills.filter((b) => String(b.id) !== editId).map((b: any) => (
                <tr key={b.id}>
                  <td><Link to={`/dashboard/procurement/purchase-bills/${b.id}`} className="text-primary-500 font-semibold hover:underline">{b.billNo}</Link></td>
                  <td>{b.supplierName}</td>
                  <td>{dmy(b.billDate)}</td>
                  <td className="num">{b.itemCount}</td>
                  <td className="num font-semibold">{formatINR(b.amount)}</td>
                  <td className="text-ink-soft text-[12px]">{b.createdBy}</td>
                  <td className="text-right whitespace-nowrap">
                    <Link to={`/dashboard/procurement/purchase-bills?edit=${b.id}`} className="btn-ghost btn-xs" title="Open this draft in the form to change it">Edit</Link>
                    <button type="button" className="btn-accent btn-xs ml-2" disabled={submittingDraftId === b.id} onClick={() => submitDraftBill(b)} title="Add this bill's items to stock as it is">
                      {submittingDraftId === b.id ? 'Submitting…' : 'Submit to stock'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <form className="card" onSubmit={(e) => handleSubmit(e, false)}>
        <div className="field-label-row">
          <h2 className="!m-0">New Purchase Bill</h2>
          {AUTO_SCAN_ENABLED && <div className="flex gap-2">
            <button
              type="button"
              className={`btn-ghost btn-sm ${entryMode === 'manual' ? 'on' : ''}`}
              onClick={() => setEntryMode('manual')}
            >
              Manual Entry
            </button>
            <button
              type="button"
              className={`btn-ghost btn-sm ${entryMode === 'upload' ? 'on' : ''}`}
              onClick={() => {
                setEntryMode('upload')
                fileInputRef.current?.click()
              }}
            >
              Upload Bill (Auto-scan)
            </button>
          </div>}
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-3 mb-1" data-testid="gst-mode">
          <label className="!m-0 text-[12px] text-ink-soft">Supplier bill type</label>
          <div className="subtab-toggle !my-0">
            <button type="button" className={gstIncluded ? 'on' : ''} onClick={() => setGstIncluded(true)}>GST bill</button>
            <button type="button" className={!gstIncluded ? 'on' : ''} onClick={() => setGstIncluded(false)}>Non-GST bill</button>
          </div>
          <span className="text-[11.5px] text-ink-soft">
            {gstIncluded ? 'Tax invoice — pick the GST % on each item; CGST + SGST are shown by rate under the sub total.' : 'Estimate / non-GST supplier — no GST at all; the grand total is just the sum of the items.'}
          </span>
          {gstIncluded && (
            <select value={taxInclusive ? 'inclusive' : 'exclusive'} onChange={(e) => chooseTaxMode(e.target.value === 'inclusive')} className={`!w-auto !py-[5px] !px-[8px] !text-xs !mb-0 ${taxInclusive ? '' : '!border-danger'}`} data-testid="tax-mode" title="Do the item rates on the supplier's bill already include GST?">
              <option value="exclusive">Rates exclude GST — GST is added on top</option>
              <option value="inclusive">Rates include GST — GST is shown, not added</option>
            </select>
          )}
          {gstIncluded && !taxInclusive && (
            <div className="w-full text-[12px] font-semibold" style={{ color: '#B42318' }} data-testid="tax-mode-warning">
              ⚠ GST will be ADDED on top of the item rates. If the rates on the supplier's bill already include GST, change this to "Rates include GST".
            </div>
          )}
        </div>

        {AUTO_SCAN_ENABLED && <div className="qr-box !bg-accent-soft">
          <div className="text-xl">📄</div>
          <div>
            <div className="qmeta2 font-semibold" style={{ color: '#8A5711' }}>
              Upload a photo or PDF of the supplier's bill
            </div>
            <div className="qmeta2">
              {isScanning
                ? 'Reading the bill and matching items to products…'
                : uploadedFile
                  ? `Selected: ${uploadedFile.name}`
                  : 'System reads it, matches items to products, and fills the table below for you to check.'}
            </div>
          </div>
          {uploadPreview && (
            <img src={uploadPreview} alt="Bill preview" className="h-14 w-14 object-cover rounded border border-line" />
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
            onChange={handleFileUpload}
            className="hidden"
          />
          <button
            type="button"
            className="btn-accent ml-auto"
            disabled={isScanning}
            onClick={() => fileInputRef.current?.click()}
          >
            {isScanning ? 'Scanning…' : 'Choose File & Auto-fill'}
          </button>
        </div>}

        <div className="row2 mt-[18px]">
          <div>
            <div className="field-label-row">
              <label className="!m-0">Supplier</label>
              <button
                type="button"
                className={`btn-ghost btn-xs ${showNewSupplier ? 'on' : ''}`}
                onClick={() => {
                  setShowNewSupplier((v) => !v)
                  setNewSupplierError('')
                }}
              >
                + Add new supplier
              </button>
            </div>
            <div className="mt-[5px]">
              <ProductPicker
                options={supplierOptions}
                text={supplierText}
                onText={typeSupplier}
                onSelect={(o) => { setSupplierText(o.label); setSelectedSupplier(Number(o.value)); setValidationErrors((prev) => { const n = { ...prev }; delete n.supplier; return n }) }}
                placeholder="Type or pick a supplier"
                className={validationErrors.supplier ? '!border-danger' : ''}
                inputProps={{ 'data-testid': 'supplier-picker' }}
                emptyText="No supplier matches — use + Add new supplier"
              />
            </div>
            {selectedSupplier === null && supplierText.trim() && <div className="hint">Pick a supplier from the list (or add it) — the typed name must match a saved supplier.</div>}
            {fieldError('supplier')}
            {(() => {
              // Same card as the customer details on the invoice page: confirms the right supplier was picked.
              const s = suppliers.find((x) => x.id === selectedSupplier)
              if (!s) return null
              return (
                <div data-testid="supplier-details" style={{ marginTop: 8, padding: '8px 10px', border: '1px solid var(--border, #ddd)', borderRadius: 8, background: 'var(--primary-soft, #f3f6fb)', fontSize: 12, lineHeight: 1.5 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{s.name}</div>
                    <button type="button" className="btn-ghost" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => setEditSupplier(true)} data-testid="edit-supplier" title="Change this supplier's name, phone, address or GSTIN">✎ Edit</button>
                  </div>
                  <div><span className="text-ink-soft">Address: </span>{s.address || s.location || <span className="text-ink-soft">not saved</span>}</div>
                  <div>
                    <span className="text-ink-soft">Phone: </span>{s.phone || <span className="text-ink-soft">not saved</span>}
                    {s.gstin ? <span className="text-ink-soft"> · GSTIN {s.gstin}</span> : <span className="text-ink-soft"> · no GSTIN (unregistered supplier?)</span>}
                    {s.email ? <span className="text-ink-soft"> · {s.email}</span> : null}
                  </div>
                </div>
              )
            })()}
            {showNewSupplier && (
              <div className="bg-primary-soft rounded-[6px] p-[14px] mt-3" data-testid="new-supplier-panel">
                <div className="row2">
                  <div>
                    <label>Supplier Name</label>
                    <input
                      type="text"
                      placeholder="e.g. Ganesh Spice Traders"
                      value={newSupplier.name}
                      onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label>Phone</label>
                    <input
                      type="text"
                      placeholder="10-digit mobile"
                      value={newSupplier.phone}
                      onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <label>
                    Address <small>— door no., street, town, PIN</small>
                  </label>
                  <textarea
                    rows={2}
                    placeholder="e.g. 14/2 Bazaar Street, Bodinayakanur — 625513"
                    value={newSupplier.address}
                    onChange={(e) => setNewSupplier({ ...newSupplier, address: e.target.value })}
                  />
                </div>
                <div className="row2 mt-3">
                  <div>
                    <label>
                      GSTIN <small>— optional</small>
                    </label>
                    <input
                      type="text"
                      value={newSupplier.gstin}
                      onChange={(e) => setNewSupplier({ ...newSupplier, gstin: e.target.value })}
                    />
                  </div>
                </div>
                {newSupplierError && <div className="hint-error">{newSupplierError}</div>}
                <div className="flex gap-2 mt-3">
                  <button type="button" className="btn-accent btn-sm" disabled={savingSupplier} onClick={handleSaveSupplier}>
                    {savingSupplier ? 'Saving…' : 'Save & Use for This Bill'}
                  </button>
                  <button
                    type="button"
                    className="btn-ghost btn-sm"
                    onClick={() => {
                      setShowNewSupplier(false)
                      setNewSupplier(EMPTY_SUPPLIER)
                      setNewSupplierError('')
                    }}
                  >
                    Cancel
                  </button>
                </div>
                <div className="hint">Saved as a permanent supplier record — appears in the list above for every future bill.</div>
              </div>
            )}
          </div>
          <div className="row2">
            <div>
              <label>Supplier Bill No.</label>
              <input type="text" value={billNo} onChange={(e) => setBillNo(e.target.value)} placeholder="e.g. SEL-4471" />
              {fieldError('billNo')}
            </div>
            <div>
              <label>Bill Date</label>
              <DateInputDMY value={billDate} testId="bill-date" onChange={(iso) => { setBillDate(iso); setValidationErrors((prev) => { const n = { ...prev }; delete n.billDate; return n }) }} className={validationErrors['billDate'] ? '!border-danger' : ''} />
              {fieldError('billDate')}
            </div>
          </div>
        </div>

        <label className="mt-[14px]">
          Link to Purchase Order <small>— optional, leave blank for an ad-hoc bill</small>
        </label>
        <select value={linkedPO || ''} onChange={(e) => applyPurchaseOrder(e.target.value ? parseInt(e.target.value) : null)}>
          <option value="">— No PO, ad-hoc bill —</option>
          {purchaseOrders.filter((po: any) => !po.status || ['Sent', 'Partially Received'].includes(po.status) || po.id === linkedPO).map((po) => (
            <option key={po.id} value={po.id}>
              {(po as any).poNumber ? `${(po as any).poNumber} — ${(po as any).supplierName} · ${(po as any).status}` : po.poNo}
            </option>
          ))}
        </select>
        <div className="hint">
          Selecting a PO pre-fills the line items below from what was ordered — you can still adjust to match what actually arrived.
        </div>

        <div className="field-label-row mt-[18px]">
          <label className="!m-0">Line Items</label>
          <button
            type="button"
            className={`btn-ghost btn-xs ${showNewProduct ? 'on' : ''}`}
            onClick={() => {
              setShowNewProduct((v) => !v)
              setNewProductError('')
            }}
          >
            + Add new product
          </button>
        </div>
        {fieldError('lineItems')}
        {showNewProduct && (
          <div className="bg-primary-soft rounded-[6px] p-[14px] mt-2 mb-2" data-testid="new-product-panel">
            <div className="row2">
              <div>
                <label>Product Name</label>
                <input
                  type="text"
                  placeholder="e.g. Cumin Seeds (Jeera)"
                  value={newProduct.name}
                  data-testid="new-product-name"
                  onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                />
              </div>
              <div>
                <label>Unit</label>
                <select value={newProduct.unit} onChange={(e) => setNewProduct({ ...newProduct, unit: e.target.value as Unit })}>
                  {UNITS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
                <div className="hint">
                  {`Bought per ${newProduct.unit === 'pkt' ? 'piece' : newProduct.unit} and sold as purchased${newProduct.unit === 'kg' ? ' — tick Repack in Settings › Products if it is repacked into packs' : ''}.`}
                </div>
              </div>
            </div>
            <div className="row3">
              <div>
                <label>
                  HSN Code <small style={{ fontWeight: 400, color: 'var(--danger, #c0392b)' }}>— required</small>
                </label>
                <input type="text" inputMode="numeric" placeholder="e.g. 09109100" maxLength={8} value={newProduct.hsnCode} data-testid="new-product-hsn" onChange={(e) => setNewProduct({ ...newProduct, hsnCode: e.target.value.replace(/\D/g, '') })} />
              </div>
            </div>
            <div className="row3">
              <div>
                <label>
                  Tamil Name <small style={{ fontWeight: 400, color: 'var(--ink-soft)' }}>— printed under the item on invoices</small>
                </label>
                <input type="text" placeholder="e.g. ஜீரகம்" value={newProduct.nameTamil} onChange={(e) => setNewProduct({ ...newProduct, nameTamil: e.target.value })} />
              </div>
              <div>
                <label>
                  Category <small style={{ fontWeight: 400, color: 'var(--ink-soft)' }}>— for category-wise reports</small>
                </label>
                <input type="text" list="product-categories" placeholder="e.g. Spices" value={newProduct.category} onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })} />
                <datalist id="product-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
              </div>
              <div>
                <label>Selling GST %</label>
                <select value={newProduct.gstRate} onChange={(e) => setNewProduct({ ...newProduct, gstRate: Number(e.target.value) })}>
                  {[0, 5, 12, 18, 28].map((r) => (
                    <option key={r} value={r}>
                      {r}%
                    </option>
                  ))}
                </select>
                <div className="hint">Selling prices are tax-inclusive; this rate is used on invoices.</div>
              </div>
            </div>
            {newProductError && <div className="hint-error">{newProductError}</div>}
            <div className="flex gap-2 mt-3">
              <button type="button" className="btn-accent btn-sm" disabled={savingProduct} onClick={handleSaveProduct}>
                {savingProduct ? 'Saving…' : 'Save & Add to Bill'}
              </button>
              <button
                type="button"
                className="btn-ghost btn-sm"
                onClick={() => {
                  setShowNewProduct(false)
                  setNewProduct(EMPTY_PRODUCT)
                  setNewProductError('')
                }}
              >
                Cancel
              </button>
            </div>
            <div className="hint">Saved to the product catalogue with its unit — future bills pre-set the unit and route automatically.</div>
          </div>
        )}
        <table className="mt-[5px]">
          <thead>
            <tr>
              <th>Product</th>
              <th>Unit</th>
              <th className="num">Qty</th>
              <th className="num">Rate</th>
              {gstIncluded && <th className="num">GST</th>}
              <th className="num">Amount</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {lineItems.map((item) => (
              <tr key={item.id}>
                <td style={{ minWidth: 230 }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <ProductPicker
                        options={pickerOptions}
                        text={item.product}
                        onText={(t) => handleProductChange(item.id, t)}
                        onSelect={(o) => handleProductChange(item.id, o.value)}
                        placeholder="Type or pick a product"
                        className={`!py-[6px] ${validationErrors[`item-${item.id}-product`] ? '!border-danger' : ''}`}
                        inputProps={{ 'data-product-line': item.id }}
                        onAddNew={(typed) => openNewProductFor(item.id, typed)}
                        addNewLabel="+ Add new product"
                      />
                    </div>
                    {products.some((x) => x.name.toLowerCase() === item.product.trim().toLowerCase()) && (
                      <div style={{ position: 'relative' }}>
                        <button type="button" className="btn-ghost" style={{ padding: '4px 9px', lineHeight: 1 }} title="Item options" aria-label="Item options" data-testid="line-menu" onClick={() => setMenuFor(menuFor === item.id ? null : item.id)}>⋯</button>
                        {menuFor === item.id && (
                          <>
                            <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setMenuFor(null)} />
                            <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 41, background: '#fff', border: '1px solid var(--border, #ddd)', borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,.15)', minWidth: 170, padding: 4 }}>
                              <button type="button" className="btn-primary" style={{ width: '100%', textAlign: 'left', marginBottom: 2 }} data-testid="line-edit-item" onClick={() => openItemModal(item, 'edit')}>✎ Edit item</button>
                              <button type="button" className="btn-ghost" style={{ width: '100%', textAlign: 'left' }} data-testid="line-view-item" onClick={() => openItemModal(item, 'view')}>View item details</button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  {(() => {
                    // Once a product is picked: its Tamil name, category and HSN, so the biller can confirm it is the right item.
                    const p = products.find((x) => x.name.toLowerCase() === item.product.trim().toLowerCase())
                    return p ? (
                      <div className="text-[10.5px] text-ink-soft mt-[3px]" data-testid="item-info">
                        {[p.nameTamil, p.category, p.hsnCode ? `HSN ${p.hsnCode}` : 'HSN not set'].filter(Boolean).join(' · ')}
                      </div>
                    ) : null
                  })()}
                  {stockHint(item.product) && (
                    <div className="text-[10.5px] text-ink-soft mt-[3px]" data-testid="stock-hint">
                      On hand: {stockHint(item.product)}
                    </div>
                  )}
                </td>
                <td>
                  <select
                    value={unitFor(item)}
                    onChange={(e) => {
                      const unit = e.target.value as Unit
                      setLineItems((items) => items.map((r) => (r.id === item.id ? { ...r, unit, route: unit === 'kg' ? (products.find((p) => p.name.toLowerCase() === r.product.trim().toLowerCase())?.defaultRoute || 'Direct Sale') : 'Direct Sale' } : r)))
                    }}
                    className="!w-24 !py-[5px] !px-[6px] !text-xs"
                    data-testid="line-unit"
                  >
                    {UNITS.map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                  {repackingEnabled && <div className="text-[10.5px] text-ink-soft mt-[3px]">{item.route === 'Repacking' ? 'to repacking' : 'sold as purchased'}</div>}
                </td>
                <td className="num">
                  <div className="inline-flex items-center gap-1">
                    <input
                      type="number"
                      min={0}
                      step={unitFor(item) === 'kg' ? '0.001' : '1'}
                      value={Number.isNaN(item.qty) ? '' : item.qty}
                      onChange={(e) => handleLineItemChange(item.id, 'qty', parseFloat(e.target.value))}
                      className={`!w-20 !py-[6px] text-right ${validationErrors[`item-${item.id}-qty`] ? '!border-danger' : ''}`}
                    />
                    <span className="text-ink-soft text-xs w-7 text-left" data-testid="qty-unit">
                      {unitFor(item)}
                    </span>
                  </div>
                </td>
                <td className="num">
                  <div className="inline-flex items-center gap-1">
                    <span className="text-ink-soft text-xs">₹</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      value={rateDisplay(item)}
                      placeholder="rate or 600/60"
                      title="Per-unit rate. You can type a calculation, e.g. 600/60 for a ₹600 bill line of 60 kg."
                      onChange={(e) => typeRate(item, e.target.value)}
                      onBlur={() => settleRate(item)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); settleRate(item) } handleRateKeyDown(e, item) }}
                      className={`!w-28 !py-[6px] text-right ${validationErrors[`item-${item.id}-rate`] || (rateDisplay(item).trim() !== '' && Number.isNaN(item.rate)) ? '!border-danger' : ''}`}
                      data-testid="rate-input"
                    />
                  </div>
                </td>
                {gstIncluded && (
                  <td className="num">
                    <select value={lineGst(item)} onChange={(e) => handleLineItemChange(item.id, 'gstRate', Number(e.target.value))} className="!w-[72px] !py-[5px] !px-[6px] !text-xs" data-testid="line-gst" title="GST % for this item (CGST + SGST). Pre-set from the product catalogue.">
                      {GST_RATES.map((r) => (
                        <option key={r} value={r}>
                          {r}%
                        </option>
                      ))}
                    </select>
                  </td>
                )}
                <td className="num font-semibold">
                  {money2((item.qty || 0) * (item.rate || 0))}
                  {/[+\-*/()]/.test(rateDisplay(item).replace(/^-/, '')) && !Number.isNaN(item.rate) && <div className="text-[10.5px] text-ink-soft font-normal">= {money2(item.rate)} / {unitFor(item)}</div>}
                </td>
                <td className="text-center">
                  <button
                    type="button"
                    onClick={() => handleRemoveLineItem(item.id)}
                    disabled={lineItems.length === 1}
                    className="text-ink-soft hover:text-danger disabled:opacity-30 text-base leading-none"
                    title="Remove line"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button type="button" className="btn-ghost mt-[10px]" onClick={handleAddLineItem}>
          + Add another item
        </button>
        <div className="hint">
          {repackingEnabled
            ? 'Unit is pre-set from the product catalogue — change it per line if this bill is different. Items bought by kg go to repacking; items bought per pkt / Tin / ltr go straight into Stock for Sale, since they’re sold exactly as purchased.'
            : 'Unit is pre-set from the product catalogue — change it per line if this bill is different. Every item goes straight into Stock for Sale in the unit it was bought in.'}
        </div>

        <div className="bill-summary mt-4" data-testid="bill-totals">
          <div className="bill-summary-row">
            <span>Sub Total <small className="text-ink-soft">({lineItems.length} item{lineItems.length === 1 ? '' : 's'}, {taxInclusive ? 'incl. GST' : 'before GST'})</small></span>
            <strong data-testid="total-value">{money2(billTotal)}</strong>
          </div>
          <div className="bill-summary-row">
            <span className="inline-flex items-center gap-2 flex-wrap">
              Discount
              <input type="number" step="0.01" min={0} value={discountInput} placeholder="0" onChange={(e) => setDiscountInput(e.target.value)} className="!w-24 !py-[5px] !px-[8px] !text-xs text-right" data-testid="discount-input" title="Discount on the bill, taken off the items before GST" />
              <select value={discountMode} onChange={(e) => setDiscountMode(e.target.value as 'percent' | 'amount')} className="!w-14 !py-[5px] !px-[4px] !text-xs" data-testid="discount-mode" title="Discount as a percentage or a rupee amount">
                <option value="percent">%</option>
                <option value="amount">₹</option>
              </select>
            </span>
            <strong data-testid="discount-amount">{discountAmount > 0 ? '- ' + money2(discountAmount) : money2(0)}</strong>
          </div>
          {gstIncluded && (discountAmount > 0 || taxInclusive) && (
            <div className="bill-summary-row">
              <span className="text-ink-soft">{taxInclusive ? 'Taxable value (excl. GST)' : 'Taxable value (after discount)'}</span>
              <span data-testid="taxable-value">{money2(taxInclusive ? taxableExclGst : taxableValue)}</span>
            </div>
          )}
          {gstIncluded && taxBreakup.filter((t) => t.rate > 0).map((t) => (
            <React.Fragment key={t.rate}>
              <div className="bill-summary-row" data-testid="tax-breakup">
                <span>CGST {t.rate / 2}% <small className="text-ink-soft">on {money2(t.taxable)}</small></span>
                <span>{money2(t.cgst)}</span>
              </div>
              <div className="bill-summary-row">
                <span>SGST {t.rate / 2}%</span>
                <span>{money2(t.sgst)}</span>
              </div>
            </React.Fragment>
          ))}
          {gstIncluded && taxBreakup.every((t) => t.rate === 0) && lineItems.some((i) => i.product.trim()) && (
            <div className="bill-summary-row"><span className="text-ink-soft">GST</span><span className="text-ink-soft">0% on every item</span></div>
          )}
          {gstIncluded && <div className="bill-summary-row"><span>{taxInclusive ? 'Total GST (included in the rates — not added)' : 'Total GST'}</span><span data-testid="gst-amount">{money2(gstAmount)}</span></div>}
          <div className="bill-summary-row">
            <span className="inline-flex items-center gap-2 flex-wrap">
              Round Off
              <input type="number" step="0.01" min={-100} max={100} value={roundOffInput} placeholder={autoRoundOff === 0 ? '0.00' : autoRoundOff.toFixed(2)} onChange={(e) => setRoundOffInput(e.target.value)} className="!w-24 !py-[5px] !px-[8px] !text-xs text-right" data-testid="round-off" title={roundOffInput.trim() === '' ? (gstIncluded && taxInclusive ? 'None — the grand total equals the sub total; type the bill’s own round-off if it has one' : 'Auto — rounds to the nearest rupee; type the bill’s own figure to match it') : 'As on the supplier bill'} />
            </span>
            <span>{roundOff < 0 ? '- ' : ''}{money2(Math.abs(roundOff))}</span>
          </div>
          <div className="bill-summary-row bill-summary-total">
            <span>Grand Total <small className="text-ink-soft">payable to supplier</small></span>
            <strong data-testid="grand-total">{money2(grandTotal)}</strong>
          </div>
        </div>
        <div className="mt-3">
          <label>Items</label>
          <input value={itemsSummary} disabled />
        </div>

        <div className="btnrow">
          <button type="submit" className="btn-primary" disabled={submitting || (!!editId && (!editingBill || editingBill.editable === false))} data-testid="submit-bill">
            {submitting ? (editId ? 'Saving…' : 'Submitting…') : editId ? (editingBill?.status === 'Draft' ? `Save draft ${editingBill?.billNo || ''}` : `Save changes to ${editingBill?.billNo || 'bill'}`) : 'Submit Purchase Bill'}
          </button>
          {editId && editingBill?.status === 'Draft' && (
            <button type="button" className="btn-accent" disabled={submitting} onClick={(e) => handleSubmit(e, false, true)} data-testid="save-and-submit" title="Save these changes and add the items to stock in one go">
              Save & submit to stock
            </button>
          )}
          {editId ? (
            <button type="button" className="btn-ghost" disabled={submitting} onClick={cancelEdit}>Cancel</button>
          ) : (
            <button type="button" className="btn-ghost" disabled={submitting} onClick={(e) => handleSubmit(e, true)} data-testid="save-draft" title="Park this bill without touching stock — it stays in the Draft bills list until you submit it">
              Save as Draft (check later)
            </button>
          )}
        </div>
        {!editId && <div className="hint">Not sure about something on the bill? Save it as a draft — nothing goes to stock until it is submitted, and it stays listed at the top of this page.</div>}
      </form>

      <div className="card mt-[18px]">
        <h2>Recent Purchase Bills</h2>
        {recentBills.length === 0 ? (
          <div className="text-center py-6 text-ink-soft text-[13px]">No purchase bills yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Bill</th>
                <th>Supplier</th>
                <th className="num">Items</th>
                <th className="num">Total Value</th>
                <th className="num">GST</th>
                <th className="num">Grand Total</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {recentBills.map((bill) => (
                <tr key={bill.id}>
                  <td>
                    <Link
                      to={`/dashboard/procurement/purchase-bills/${bill.id}`}
                      className="text-primary-500 font-semibold hover:underline"
                      title="Open bill details"
                    >
                      {bill.billNo}
                    </Link>
                  </td>
                  <td>{bill.supplierName}</td>
                  <td className="num">{bill.itemCount}</td>
                  <td className="num">{formatINR(bill.taxableAmount ?? bill.amount)}</td>
                  <td className="num">
                    {formatINR(bill.gstAmount ?? 0)}
                    <div className="text-[10.5px] text-ink-soft">{bill.gstRate ?? 0}%</div>
                  </td>
                  <td className="num font-semibold">{formatINR(bill.amount)}</td>
                  <td>
                    <span className={`badge ${statusBadge(bill.status)}`}>{bill.status}</span>
                    {bill.status === 'Draft' && (
                      <button type="button" className="btn-accent btn-xs ml-2" disabled={submittingDraftId === bill.id} onClick={() => submitDraftBill(bill)} title="Add this bill's items to stock">
                        {submittingDraftId === bill.id ? 'Submitting…' : 'Submit to stock'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="flex justify-end mt-[10px]">
          <Link to="/dashboard/procurement/purchase-bills/list" className="btn-ghost btn-xs">
            View all bills →
          </Link>
        </div>
        <div className="hint">
          On submission: Repacking lines create bulk stock batches immediately (enter the repacking pipeline); Direct Sale lines go
          straight into Stock for Sale. No separate approval step — single-entry, effective right away.
        </div>
      </div>
    </div>
  )
}
